// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "config/stripe";
import { LocationProvider } from "context/Location";
import { isDevEnvironment } from "utils/isDevEnvironment";
import { useAuth } from "hooks/useAuth";
import { ColorModeThemeProvider as Themed } from "context/ColorMode";

// Auth pages
import AuthPage from "pages/auth/AuthPage";
import MorningReportLandingPage from "features/morningReport/pages/MorningReportLandingPage";
import {
  MorningReportGuestStart,
  MorningReportWorkspace,
} from "features/morningReport/MorningReportPages";
import { MORNING_REPORT_PATH, isMorningReportDomain } from "features/morningReport/morningReportPaths";
import ResetPasswordPage from "pages/auth/ResetPasswordPage";
import WPAuthHandler from "pages/auth/WPAuthHandler";
import LinkDevicePage from "pages/auth/LinkDevicePage";
import PhoneRecordingPage from "pages/chartmind/PhoneRecordingPage";

// Dashboard pages
import DashboardPage from "pages/dashboard/DashboardPage";
import KijabeDashboard from "pages/dashboard/KijabeDashboard";

// Chat pages
import KijabeChatPage from "pages/KijabeChatPage";
import ChatPageWrapper from "pages/ChatPageWrapper";
import AskMeAnythingPage from "pages/AskMeAnythingPage";

// Virtual Queue pages
import PatientRegistration from "components/vaccine/queue/PatientRegistration";

// Legal pages
import PrivacyPolicy from "components/auth/documents/PrivacyPolicy";
import TermsOfUse from "components/auth/documents/TermsOfUse";
import Baa from "components/auth/documents/baa";

// Account pages
import Verify from "components/dashboard/Verify";
import Onboarding from "components/auth/Onboarding";

import JoinPage from "pages/invitation/JoinPage";
import CheckInPage from "pages/checkin/CheckInPage";
import ShareLandingPage from "pages/share/ShareLandingPage";
import EhrCallbackPage from "pages/dashboard/individual/health-records/EhrCallbackPage";
import DischargeHandoffPage from "pages/discharge/DischargeHandoffPage";
import ApiDemoPage from "pages/apiDemo/ApiDemoPage";
// Static, unlike the page itself: this provider holds the in-flight check-in, so it
// must not arrive with the lazy chunk. Same reasoning as PatientRoutes.
import { VisitStartDraftProvider } from "features/visitStart/visitStartDraftStore";

// CareStart voice triage (public, anonymous). Lazy-loaded so the voice engine +
// audio bundle stays out of the main chunk.
import Loading from "components/Loading";
const CareStartPage = React.lazy(() => import("features/careStart/CareStartPage"));

// VisitStart pre-visit intake (public, guest-capable). Lazy for the same reason as
// CareStart: it pulls in react-hook-form and the element catalog.
const VisitStartPage = React.lazy(() => import("features/visitStart/VisitStartPage"));

// CareMap, the full-screen facility map. Lazy: it pulls in Leaflet and the map tiles.
const CareMapPage = React.lazy(() => import("features/careMap/CareMapPage"));
// DEV-ONLY, uncommitted: the old map, to compare against CareMap.
// const MapPage = React.lazy(() => import("pages/map/MapPage"));

const RootRedirect = () => {
  const { user, userLoading } = useAuth();
  const { pathname } = useLocation();
  if (userLoading) return null;
  // An anonymous user is a guest on a public page (CareStart, VisitStart), not a
  // signed-in one. Sending them to /dashboard starts a loop: DashboardPage finds
  // no user doc and pushes them to /onboarding, which bounces them to /auth,
  // which parks them on "verify your email" for an account that has no email.
  const signedIn = Boolean(user) && !user.isAnonymous;
  // On morningreport.si the root is the MorningReport landing page (no /morning-report
  // in the address), and anything unknown comes back to it.
  if (isMorningReportDomain()) {
    if (signedIn) return <Navigate to={MORNING_REPORT_PATH} replace />;
    if (pathname !== "/") return <Navigate to="/" replace />;
    return <Themed><MorningReportLandingPage /></Themed>;
  }
  return <Navigate to={signedIn ? "/dashboard" : "/auth"} replace />;
};

// Pages wrapped in <Themed> follow the dark mode setting (the dashboard does too, via
// DashboardNav). The rest are legacy styled-components pages that only exist in light.
export const AppRoutes = () => (
  <Routes>
    <Route
      path="/dashboard/*"
      element={
        <Elements stripe={stripePromise}>
          <LocationProvider>
            <DashboardPage />
          </LocationProvider>
        </Elements>
      }
    />

    <Route path="/auth" element={<Themed><AuthPage /></Themed>} />
    <Route path="/login" element={<Navigate to="/auth" replace />} />

    {/* Desktop sign-in by phone approval. No `?c=` is the computer showing the QR;
        with one it is the phone that scanned it. Public on both sides: the computer
        has no session yet, and the phone brings its own. */}
    <Route path="/link-device" element={<Themed><LinkDevicePage /></Themed>} />

    {/* ChartMind phone microphone. Scanned from a signed-in desktop, so `?p=` names the
        pairing, not the account: signing a phone in does not route a transcript. Flat rather
        than under /dashboard on purpose — the dashboard shell would give the phone its own
        ChartMind session, and it needs a microphone, not a second copy of the app. */}
    <Route path="/chartmind-mic" element={<Themed><PhoneRecordingPage /></Themed>} />
    
    {/* Organization Invitation */}
    <Route path="/join/:token" element={<Themed><JoinPage /></Themed>} />

    {/* Public clinic check-in landing (scanned QR / shared link) */}
    <Route path="/checkin/:clinicId" element={<Themed><CheckInPage /></Themed>} />

    {/* Where a hospital's SMART on FHIR login returns. Top level rather than
        under /dashboard because the path is registered with each provider and
        changing it later means re-registering the app. */}
    <Route path="/ehr/callback" element={<Themed><EhrCallbackPage /></Themed>} />

    {/* Public landing for a patient-shared medical record (scanned QR) */}
    <Route path="/share" element={<Themed><ShareLandingPage /></Themed>} />

    {/* Public, guest-capable: a patient's phone scanning the discharge QR a provider
        shows from ChartMind when no record was imported. */}
    <Route path="/discharge" element={<Themed><DischargeHandoffPage /></Themed>} />

    {/* SANDBOX DEMO ONLY. Partner API demo: plays the part of a clinic's HMIS, using the
        partner's own key. It is for showing customers the API on sandbox and must not be
        reachable in production, so it is only registered in dev and sandbox builds. */}
    {isDevEnvironment() && <Route path="/api-demo" element={<ApiDemoPage />} />}

    {/* MorningReport learner sign-up. Public, and open to new accounts even while
        sign-ups are invitation only (see fromLearnerSignup in AuthPage). */}
    <Route path="/morning-report" element={<Themed><MorningReportLandingPage /></Themed>} />
    {/* Today's case for guests (anonymous, no email), outside the dashboard. */}
    <Route path="/morning-report/play" element={<Themed><MorningReportGuestStart /></Themed>} />
    <Route path="/morning-report/play/:sessionId" element={<Themed><MorningReportWorkspace /></Themed>} />

    {/* Legacy registration routes - redirect to new auth with role param */}
    <Route path="/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/patient" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/register/provider" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/register/facility" element={<Navigate to="/auth?role=facility" replace />} />
    <Route path="/msi/register" element={<Navigate to="/auth?role=patient" replace />} />
    <Route path="/pro/register" element={<Navigate to="/auth?role=provider" replace />} />
    <Route path="/facility/register" element={<Navigate to="/auth?role=facility" replace />} />

    {/* Other auth routes */}
    <Route path="/reset-password" element={<Themed><ResetPasswordPage /></Themed>} />
    <Route path="/wp-auth-handler" element={<Themed><WPAuthHandler /></Themed>} />

    {/* Account Routes */}
    <Route path="/account" element={<Themed><Verify /></Themed>} />
    <Route path="/onboarding" element={<Themed><Onboarding /></Themed>} />

    {/* Chat Routes */}
    <Route path="/kijabe-dashboard" element={<KijabeDashboard />} />
    <Route path="/kijabe" element={<KijabeChatPage />} />
    <Route path="/chat" element={<ChatPageWrapper />} />

    {/* Dev-only: WP Ask Me Anything embed preview */}
    {isDevEnvironment() && (
      <Route path="/ask-me-anything" element={<AskMeAnythingPage />} />
    )}

    {/* Virtual Queue Routes */}
    <Route path="/registration/*" element={<PatientRegistration />} />

    {/* Legal Routes */}
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-use" element={<TermsOfUse />} />
    <Route path="/baa" element={<Baa />} />
    {/* <Route path="/chat" element={<PublicChatPage />} /> */}

    {/* CareStart: public anonymous voice triage (English MVP). */}
    <Route
      path="/carestart"
      element={
        <Themed>
          <LocationProvider>
            <React.Suspense fallback={<Loading page />}>
              <CareStartPage />
            </React.Suspense>
          </LocationProvider>
        </Themed>
      }
    />

    {/* VisitStart: public pre-visit intake, reached by scanning a clinic's code.
        `allowGuest` is the whole difference from the dashboard mount — a visitor
        with no account is signed in anonymously rather than turned away, and the
        frame owns the viewport instead of the dashboard content area.

        The draft provider is here for the same reason PatientRoutes mounts one:
        the elements keep their in-progress form state in it, so going Back to an
        earlier question returns what the patient had typed. */}
    <Route
      path="/visitstart"
      element={
        <Themed>
          <VisitStartDraftProvider>
            <React.Suspense fallback={<Loading page />}>
              <VisitStartPage allowGuest />
            </React.Suspense>
          </VisitStartDraftProvider>
        </Themed>
      }
    />
    <Route
      path="/visitstart/:locationId"
      element={
        <Themed>
          <VisitStartDraftProvider>
            <React.Suspense fallback={<Loading page />}>
              <VisitStartPage allowGuest />
            </React.Suspense>
          </VisitStartDraftProvider>
        </Themed>
      }
    />

    {/* CareMap: dev servers only while it is tested. The old /map page stays
        retired: its quality scores and filters are built around US Medicare data,
        which says nothing about the Kenyan facilities. CareMap shows no scores.
        Reached from the nine-dot launcher (AppSwitcher), not the sidebar. */}
    {isDevEnvironment() && (
      <Route
        path="/caremap"
        element={
          <Themed>
            {/* For Settings, opened from the account menu. */}
            <LocationProvider>
              <React.Suspense fallback={<Loading page />}>
                <CareMapPage />
              </React.Suspense>
            </LocationProvider>
          </Themed>
        }
      />
    )}

    {/* {isDevEnvironment() && (
      <Route
        path="/map"
        element={
          <LocationProvider>
            <React.Suspense fallback={<Loading page />}>
              <MapPage />
            </React.Suspense>
          </LocationProvider>
        }
      />
    )} */}

    {/* The root is an entry point, not a destination: signed out goes to login,
        signed in goes to the dashboard. Same for anything unrecognised. */}
    <Route path="/" element={<RootRedirect />} />
    <Route path="*" element={<RootRedirect />} />
  </Routes>
);
